import { useMutation, useQueryClient } from "@tanstack/vue-query";
import { useRouteParams } from "@vueuse/router";
import { useContentCommandApi } from "@/composables/useContentCommandApi";
import { FlowCode, StatusCode, TranslationType } from "@/types/generated/ContentCommandApi";
import {
  useCurrentExperienceTranslationsQuery,
  EXPERIENCE_TRANSLATIONS_QUERY_KEY,
} from "@/features/experience-curation/queries/useExperienceTranslationsQuery";
import { useCurationTitle } from "@/features/experience-curation/sections/settings/title/useCurationTitle";
import { useCurationSeoTitle } from "@/features/experience-curation/sections/settings/seo-title/useCurationSeoTitle";
import { useCurationDescription } from "@/features/experience-curation/sections/content-generation/description/useCurationDescription";
import { useCurationSeoDescription } from "@/features/experience-curation/sections/content-generation/seo-description/useCurationSeoDescription";
import { useCurationAdditionalDescription } from "@/features/experience-curation/sections/content-generation/additional-description/useCurationAdditionalDescription";
import { useCurationInfoVoucher } from "@/features/experience-curation/sections/customer-info/info-voucher/useCurationInfoVoucher";
import { useCurationMeetingPoint } from "@/features/experience-curation/sections/location/meeting-point/useCurationMeetingPoint";

/**
 * Mutation responsible for persisting the experience translation (all text fields).
 * Reads from the individual field stores that manage each text field.
 */
export function useCurationTranslationsSaveMutation() {
  const experienceId = useRouteParams<string>("id");
  const queryClient = useQueryClient();
  const { putTranslation } = useContentCommandApi();

  const translationsQuery = useCurrentExperienceTranslationsQuery();

  const titleStore = useCurationTitle();
  const seoTitleStore = useCurationSeoTitle();
  const descriptionStore = useCurationDescription();
  const seoDescriptionStore = useCurationSeoDescription();
  const additionalDescriptionStore = useCurationAdditionalDescription();
  const infoVoucherStore = useCurationInfoVoucher();
  const meetingPointStore = useCurationMeetingPoint();

  return useMutation({
    mutationFn: async () => {
      const translationId = translationsQuery.data.value?.id;
      if (!translationId) {
        throw new Error("Translation ID not available");
      }

      await putTranslation(`experience-translations/${translationId}`, {
        experience_id: experienceId.value,
        title: titleStore.model,
        seo_title: seoTitleStore.model,
        seo_description: seoDescriptionStore.model,
        text1: descriptionStore.model,
        text2: additionalDescriptionStore.model,
        info_voucher: infoVoucherStore.model,
        meeting_point_details: meetingPointStore.model,
        language_code: "en",
        translation_type: TranslationType.MANUAL,
        curation_quality: false,
        status_code: StatusCode.IN_REVIEW,
        flow_code: FlowCode.CURATION,
      });
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: [EXPERIENCE_TRANSLATIONS_QUERY_KEY] });
    },
  });
}
